import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Compass, CheckCircle2, Loader2 } from 'lucide-react';
/**
 * Quote submissions are delivered by Web3Forms — a free, no-backend form API
 * that emails every submission straight to your inbox.
 *
 * SETUP (one-time, ~1 minute):
 *  1. Go to https://web3forms.com
 *  2. Enter the destination email: compassmovingtexas@gmail.com
 *  3. Web3Forms emails you a free Access Key (a UUID like 1a2b3c4d-....)
 *  4. Paste that key below, replacing YOUR_ACCESS_KEY_HERE.
 *
 * Submissions then arrive at compassmovingtexas@gmail.com with the subject
 * "New Quote Request – Compass Moving Website". No account or billing needed.
 */
const WEB3FORMS_ACCESS_KEY = 'bcabdae8-2d36-414a-913a-e44a6aa7580a';
const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const RECIPIENT_EMAIL = 'compassmovingtexas@gmail.com';
interface FormState {
  fullName: string;
  phone: string;
  email: string;
  movingFrom: string;
  movingTo: string;
  moveDate: string;
  details: string;
}
const initialForm: FormState = {
  fullName: '',
  phone: '',
  email: '',
  movingFrom: '',
  movingTo: '',
  moveDate: '',
  details: ''
};
type Status = 'idle' | 'submitting' | 'success' | 'error';
export function Contact() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [status, setStatus] = useState<Status>('idle');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (WEB3FORMS_ACCESS_KEY === 'YOUR_ACCESS_KEY_HERE') {
      // Access key not configured yet — fail gracefully with guidance.
      console.warn('Web3Forms access key is not set. Get a free key at https://web3forms.com (use compassmovingtexas@gmail.com) and paste it into WEB3FORMS_ACCESS_KEY in components/Contact.tsx.');
      setStatus('error');
      return;
    }
    setStatus('submitting');
    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: 'New Quote Request – Compass Moving Website',
      from_name: 'Compass Moving Website',
      // Reply-To is set to the visitor's email so you can respond directly.
      replyto: form.email,
      'Full Name': form.fullName,
      'Phone Number': form.phone,
      'Email Address': form.email,
      'Moving From': form.movingFrom,
      'Moving To': form.movingTo,
      'Estimated Move Date': form.moveDate,
      'Additional Details': form.details
    };
    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus('success');
        setForm(initialForm);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };
  const inputClasses = 'w-full px-4 py-3 rounded border border-gray-200 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors';
  return <section id="contact" className="py-24 bg-gray-light" data-id="element-44">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-45">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-46">
          {/* Contact Form */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="bg-white p-8 md:p-10 rounded-lg shadow-xl border-t-4 border-gold" data-id="element-47">
            <h3 className="text-3xl font-serif text-navy font-bold mb-2" data-id="element-48">
              Request a Free Quote
            </h3>
            <p className="text-gray-dark/70 mb-8" data-id="element-49">
              Tell us about your move and one of our moving specialists will
              contact you shortly. Whether you're moving across town or across
              the country, we're here to guide you every step of the way.
            </p>

            {status === 'success' ? <div role="status" className="flex flex-col items-center text-center py-12" data-id="element-50">
                <div className="w-16 h-16 rounded-full bg-gold/15 flex items-center justify-center mb-6" data-id="element-51">
                  <CheckCircle2 className="w-9 h-9 text-gold" data-id="element-52" />
                </div>
                <h4 className="text-2xl font-serif font-bold text-navy mb-3" data-id="element-53">
                  Request Received
                </h4>
                <p className="text-gray-dark/70 max-w-sm" data-id="element-54">
                  Thank you for contacting Compass Moving! We received your
                  request and will reach out shortly.
                </p>
                <button type="button" onClick={() => setStatus('idle')} className="mt-8 text-navy font-semibold underline decoration-gold underline-offset-4 hover:text-gold transition-colors" data-id="element-55">
                  Submit another request
                </button>
              </div> : <form className="space-y-6" onSubmit={handleSubmit} noValidate data-id="element-56">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-57">
                  <div data-id="element-58">
                    <label htmlFor="fullName" className="block text-sm font-medium text-navy mb-2" data-id="element-59">
                      Full Name
                    </label>
                    <input id="fullName" name="fullName" type="text" required value={form.fullName} onChange={handleChange} className={inputClasses} placeholder="John Doe" data-id="element-60" />
                  </div>
                  <div data-id="element-61">
                    <label htmlFor="phone" className="block text-sm font-medium text-navy mb-2" data-id="element-62">
                      Phone Number
                    </label>
                    <input id="phone" name="phone" type="tel" required value={form.phone} onChange={handleChange} className={inputClasses} placeholder="(555) 123-4567" data-id="element-63" />
                  </div>
                </div>

                <div data-id="element-64">
                  <label htmlFor="email" className="block text-sm font-medium text-navy mb-2" data-id="element-65">
                    Email Address
                  </label>
                  <input id="email" name="email" type="email" required value={form.email} onChange={handleChange} className={inputClasses} placeholder="john@example.com" data-id="element-66" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-67">
                  <div data-id="element-68">
                    <label htmlFor="movingFrom" className="block text-sm font-medium text-navy mb-2" data-id="element-69">
                      Moving From (Zip/City)
                    </label>
                    <input id="movingFrom" name="movingFrom" type="text" value={form.movingFrom} onChange={handleChange} className={inputClasses} placeholder="Dallas, TX" data-id="element-70" />
                  </div>
                  <div data-id="element-71">
                    <label htmlFor="movingTo" className="block text-sm font-medium text-navy mb-2" data-id="element-72">
                      Moving To (Zip/City)
                    </label>
                    <input id="movingTo" name="movingTo" type="text" value={form.movingTo} onChange={handleChange} className={inputClasses} placeholder="Denver, CO" data-id="element-73" />
                  </div>
                </div>

                <div data-id="element-74">
                  <label htmlFor="moveDate" className="block text-sm font-medium text-navy mb-2" data-id="element-75">
                    Estimated Move Date
                  </label>
                  <input id="moveDate" name="moveDate" type="date" value={form.moveDate} onChange={handleChange} className={`${inputClasses} text-gray-dark`} data-id="element-76" />
                </div>

                <div data-id="element-77">
                  <label htmlFor="details" className="block text-sm font-medium text-navy mb-2" data-id="element-78">
                    Additional Details
                  </label>
                  <textarea id="details" name="details" rows={4} value={form.details} onChange={handleChange} className={`${inputClasses} resize-none`} placeholder="Tell us about your move (home size, apartment, office relocation, special items, packing needs, etc.)." data-id="element-79"></textarea>
                </div>

                {status === 'error' && <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-4 py-3" data-id="element-80">
                    Something went wrong sending your request. Please try again,
                    or email us directly at{' '}
                    <a href={`mailto:${RECIPIENT_EMAIL}`} className="font-semibold underline" data-id="element-81">
                      {RECIPIENT_EMAIL}
                    </a>
                    .
                  </p>}

                <button type="submit" disabled={status === 'submitting'} className="w-full bg-gold hover:bg-gold-light text-navy font-bold text-lg py-4 rounded transition-colors shadow-md disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-82">
                  {status === 'submitting' ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-83" />
                      Sending...
                    </> : 'Submit Request'}
                </button>
              </form>}
          </motion.div>

          {/* Contact Info */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="flex flex-col justify-center" data-id="element-84">
            <h2 className="text-gold font-sans uppercase tracking-[0.2em] font-semibold text-sm mb-4" data-id="element-85">
              Get In Touch
            </h2>
            <h3 className="text-4xl font-serif text-navy font-bold mb-8" data-id="element-86">
              We're Here to Guide Your Move
            </h3>

            <div className="space-y-8" data-id="element-87">
              <div className="flex items-start gap-4" data-id="element-88">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-89">
                  <MapPin className="w-6 h-6 text-gold" data-id="element-90" />
                </div>
                <div data-id="element-91">
                  <h4 className="text-xl font-serif font-bold text-navy mb-1" data-id="element-92">
                    Headquarters
                  </h4>
                  <p className="text-gray-dark/80" data-id="element-93">
                    Dallas, Texas
                    <br data-id="element-94" />
                    Serving All 50 States Nationwide
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-95">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-96">
                  <Phone className="w-6 h-6 text-gold" data-id="element-97" />
                </div>
                <div data-id="element-98">
                  <h4 className="text-xl font-serif font-bold text-navy mb-1" data-id="element-99">
                    Phone
                  </h4>
                  <p className="text-gray-dark/80" data-id="element-100">
                    <a href="tel:2146094606" className="hover:text-gold transition-colors" data-id="element-101">
                      214-609-4606
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-102">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-103">
                  <Mail className="w-6 h-6 text-gold" data-id="element-104" />
                </div>
                <div data-id="element-105">
                  <h4 className="text-xl font-serif font-bold text-navy mb-1" data-id="element-106">
                    Email
                  </h4>
                  <p className="text-gray-dark/80" data-id="element-107">
                    <a href={`mailto:${RECIPIENT_EMAIL}`} className="hover:text-gold transition-colors break-all" data-id="element-108">
                      {RECIPIENT_EMAIL}
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-109">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-110">
                  <Clock className="w-6 h-6 text-gold" data-id="element-111" />
                </div>
                <div data-id="element-112">
                  <h4 className="text-xl font-serif font-bold text-navy mb-1" data-id="element-113">
                    Business Hours
                  </h4>
                  <p className="text-gray-dark/80" data-id="element-114">
                    Monday – Friday: 8:00 AM – 6:00 PM
                    <br data-id="element-115" />
                    Saturday: 9:00 AM – 2:00 PM
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 p-8 bg-navy rounded-lg text-white relative overflow-hidden" data-id="element-116">
              <div className="absolute -right-6 -top-6 text-gold/10" data-id="element-117">
                <Compass className="w-40 h-40" strokeWidth={1} data-id="element-118" />
              </div>
              <div className="relative" data-id="element-119">
                <div className="flex items-center gap-3 mb-3" data-id="element-120">
                  <Compass className="w-6 h-6 text-gold" data-id="element-121" />
                  <h4 className="font-serif font-bold text-xl text-gold" data-id="element-122">
                    Moving What Matters Most
                  </h4>
                </div>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-123">
                  At Compass Moving, we believe every move represents a new
                  beginning. From your first phone call to the final delivery,
                  we treat your home, memories, and belongings with the same
                  care and respect as if they were our own.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}